import Modal from "components/elements/Modal";
import classes from "./classes.module.scss";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import Button, { EButtonVariant, EButtonColor } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import Select from "components/materials/Form/SelectElement";
import Input from "components/materials/Form/InputElement";
import DragAndDropFile from "components/materials/Form/DragAndDropFileElement";
import Form from "components/materials/Form";
import MediaRequestResource from "common/resources/Project/Config/Media/MediaRequestResource";
import { I18nTextResource } from "common/resources/I18nText/I18nTextResource";
import { EMediaKind } from "common/enums/Project/Config/Media/EMediaKind";
import { ValidationError } from "common/resources/Resource";
import MediaService from "services/projects/MediaService";
import { container } from "tsyringe";
import { formatUrl } from "utils/StringUtils";

const mediaService = container.resolve(MediaService);

type IProps = {
	projectId: string;
	isOpen: boolean;
	onCreatedMedia: () => void;
	onClose: () => void;
};

export default function AddMediaModal(props: IProps) {
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [mediaType, setMediaType] = useState<EMediaKind>(EMediaKind.IMAGE);

	const onSelectChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
		setMediaType(event.target.value as EMediaKind);
	}, []);

	const onSubmit = useCallback(
		(formData: { [key: string]: unknown }) => {
			setErrors([]);

			console.log({ formData });
			const caption = "caption" in formData ? (formData["caption"] as string) : null;
			if (caption && caption.length > 100) {
				setErrors(getCustomTitleError());
				return;
			}

			const hydratedCaption = caption
				? I18nTextResource.hydrateArray<I18nTextResource>([
						{
							lang: I18n.getLang(),
							text: caption,
						},
					])
				: undefined;

			const hydratedMedia = MediaRequestResource.hydrate<MediaRequestResource>({
				caption: hydratedCaption,
				kind: mediaType,
				previewImageUrl: "previewImageUrl" in formData ? (formData["previewImageUrl"] as string) : undefined,
				url: "url" in formData ? formatUrl(formData["url"] as string) : undefined,
			});

			const file = (formData["file"] as FileList)?.[0];

			mediaService
				.create(props.projectId, hydratedMedia, file)
				.then(() => {
					props.onCreatedMedia();
				})
				.catch((error) => {
					console.error({ error });
					if (error instanceof Array) {
						setErrors(error);
					}
				});
		},
		[mediaType, props],
	);

	useEffect(() => {
		if (props.isOpen === false) {
			setMediaType(EMediaKind.IMAGE);
		}
	}, [props]);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.media.add_media_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form className={classes["form-container"]} onSubmit={onSubmit} errors={errors}>
				<Select
					name="type"
					onChange={onSelectChange}
					label={I18n.asset.pages.professional_space.tabs.media.add_media_modal.type}
					defaultValue={mediaType}
					options={getSelectOptions()}
					className={classes["input"]}
				/>

				<Input
					name="caption"
					label={I18n.asset.pages.professional_space.tabs.media.add_media_modal.media_title}
					additionalInfo={I18n.asset.pages.professional_space.tabs.media.add_media_modal.media_title_max_length}
					className={classes["input"]}
				/>

				{(mediaType === EMediaKind.URL || mediaType === EMediaKind.VIDEO) && (
					<Input name="url" label={I18n.asset.pages.professional_space.tabs.media.add_media_modal.url} className={classes["input"]} />
				)}

				{mediaType === EMediaKind.IMAGE && (
					<DragAndDropFile
						name="file"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.media.add_media_modal.drag_and_drop.image}
						description={I18n.asset.pages.professional_space.tabs.media.add_media_modal.drag_and_drop.description}
						className={classes["drag-and-drop"]}
					/>
				)}

				{(mediaType === EMediaKind.URL || mediaType === EMediaKind.VIDEO) && (
					<DragAndDropFile
						name="file"
						icon={<DocumentArrowUpIcon className={classes["drag-and-drop-icon"]} />}
						accept={["image/png", "image/jpeg", "image/webp"]}
						sub_description={`(max. 5${I18n.asset.unit.megabyte})`}
						maxMbSize={5}
						label={I18n.asset.pages.professional_space.tabs.media.add_media_modal.drag_and_drop.image_link}
						description={I18n.asset.pages.professional_space.tabs.media.add_media_modal.drag_and_drop.description}
						className={classes["drag-and-drop"]}
					/>
				)}

				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onClose}>
						{I18n.asset.pages.professional_space.tabs.media.add_media_modal.cancel}
					</Button>
					<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} type="submit">
						{I18n.asset.pages.professional_space.tabs.media.add_media_modal.add_media}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}

function getSelectOptions() {
	return Object.values(EMediaKind).map((value) => ({ value, label: I18n.asset.pages.professional_space.tabs.media.add_media_modal[value] }));
}

function getCustomTitleError() {
	return [
		{
			property: "title",
			constraints: {
				maxLength: "The text length must not exceed {{var1}} characters.",
			},
			contexts: {
				maxLength: [100],
			},
		},
	];
}
